import { Shield, Zap, Wrench, Radio, Microscope, Target, Heart, Eye, Satellite, Brain, Cpu, Lock } from 'lucide-react';

// Research Tree organized by tiers
export const RESEARCH_TIERS = {
  EARLY: 'early',
  MID: 'mid',
  LATE: 'late'
};

export const RESEARCH_CATEGORIES = {
  DEFENSE: 'defense',
  OFFENSE: 'offense',
  SUPPORT: 'support',
  TACTICS: 'tactics',
  COMMAND: 'command',
  TECH: 'tech'
};

export const researchTree = {
  // ===== TIER 1: EARLY GAME (Foundation) =====
  early: [
    {
      id: 'basic_armor',
      name: 'Basic Armor Plating',
      tier: RESEARCH_TIERS.EARLY,
      category: RESEARCH_CATEGORIES.DEFENSE,
      icon: Shield,
      description: 'Develop improved body armor with ceramic plating to reduce casualties',
      cost: { funds: 8000, supplies: 30, intel: 10 },
      duration: 3,
      prerequisites: [],
      benefits: [
        '+10% soldier health',
        '-10% mission casualties',
        'Unlocks: Basic Armor Equipment'
      ],
      unlocks: ['advanced_armor', 'tactical_vest']
    },
    {
      id: 'weapon_training',
      name: 'Advanced Weapon Training',
      tier: RESEARCH_TIERS.EARLY,
      category: RESEARCH_CATEGORIES.OFFENSE,
      icon: Target,
      description: 'Improve soldier marksmanship and weapon handling',
      cost: { funds: 6000, supplies: 20, intel: 5 },
      duration: 2,
      prerequisites: [],
      benefits: [
        '+15% mission success rate',
        '+10% accuracy',
        'Unlocks: Training Programs'
      ],
      unlocks: ['weapon_mods', 'sniper_training']
    },
    {
      id: 'field_medicine',
      name: 'Field Medicine',
      tier: RESEARCH_TIERS.EARLY,
      category: RESEARCH_CATEGORIES.SUPPORT,
      icon: Heart,
      description: 'Train medics in advanced field medicine and triage techniques',
      cost: { funds: 7000, supplies: 25, intel: 8 },
      duration: 3,
      prerequisites: [],
      benefits: [
        '+20% healing efficiency',
        'Faster recovery times',
        'Unlocks: Medical Equipment'
      ],
      unlocks: ['combat_medic', 'mobile_hospital']
    },
    {
      id: 'basic_recon',
      name: 'Basic Reconnaissance',
      tier: RESEARCH_TIERS.EARLY,
      category: RESEARCH_CATEGORIES.TACTICS,
      icon: Eye,
      description: 'Establish recon protocols and surveillance techniques',
      cost: { funds: 5000, supplies: 15, intel: 12 },
      duration: 2,
      prerequisites: [],
      benefits: [
        '+25% intel gathering',
        'Better mission intel',
        'Unlocks: Recon Missions'
      ],
      unlocks: ['stealth_ops', 'surveillance_network']
    }
  ],

  // ===== TIER 2: MID GAME (Specialization) =====
  mid: [
    {
      id: 'advanced_armor',
      name: 'Advanced Armor Systems',
      tier: RESEARCH_TIERS.MID,
      category: RESEARCH_CATEGORIES.DEFENSE,
      icon: Shield,
      description: 'Next-generation armor with reactive plating and shock absorption',
      cost: { funds: 15000, supplies: 50, intel: 20 },
      duration: 5,
      prerequisites: ['basic_armor'],
      benefits: [
        '+25% soldier health',
        '-20% mission casualties',
        '+10% explosion resistance',
        'Unlocks: Heavy Armor Equipment'
      ],
      unlocks: ['powered_exoskeleton', 'energy_shields']
    },
    {
      id: 'weapon_mods',
      name: 'Weapon Modifications',
      tier: RESEARCH_TIERS.MID,
      category: RESEARCH_CATEGORIES.OFFENSE,
      icon: Zap,
      description: 'Upgrade weapons with improved targeting systems and firepower',
      cost: { funds: 12000, supplies: 40, intel: 15 },
      duration: 4,
      prerequisites: ['weapon_training'],
      benefits: [
        '+25% weapon damage',
        '+20% accuracy',
        'Reduced reload times',
        'Unlocks: Advanced Weapons'
      ],
      unlocks: ['plasma_weapons', 'strategic_planning']
    },
    {
      id: 'combat_medic',
      name: 'Combat Medic Program',
      tier: RESEARCH_TIERS.MID,
      category: RESEARCH_CATEGORIES.SUPPORT,
      icon: Wrench,
      description: 'Train specialized combat medics for field operations',
      cost: { funds: 10000, supplies: 35, intel: 10 },
      duration: 4,
      prerequisites: ['field_medicine'],
      benefits: [
        '+35% healing efficiency',
        'Medics can revive in combat',
        '+15% soldier survivability',
        'Unlocks: Advanced Medical Tech'
      ],
      unlocks: ['nano_medicine', 'mobile_hospital']
    },
    {
      id: 'stealth_ops',
      name: 'Stealth Operations',
      tier: RESEARCH_TIERS.MID,
      category: RESEARCH_CATEGORIES.TACTICS,
      icon: Radio,
      description: 'Develop stealth tactics and equipment for covert missions',
      cost: { funds: 18000, supplies: 55, intel: 30 },
      duration: 6,
      prerequisites: ['basic_recon'],
      benefits: [
        'Unlock stealth missions',
        '-40% detection chance',
        '+30% infiltration success',
        'Unlocks: Covert Equipment'
      ],
      unlocks: ['ghost_protocol', 'cyber_warfare']
    },
    {
      id: 'surveillance_network',
      name: 'Surveillance Network',
      tier: RESEARCH_TIERS.MID,
      category: RESEARCH_CATEGORIES.TECH,
      icon: Satellite,
      description: 'Deploy satellite and drone surveillance systems',
      cost: { funds: 16000, supplies: 45, intel: 35 },
      duration: 5,
      prerequisites: ['basic_recon'],
      benefits: [
        '+40% intel gathering',
        'Real-time mission data',
        'Enemy position tracking',
        'Unlocks: Drone Support'
      ],
      unlocks: ['ai_tactical', 'strategic_planning']
    },
    {
      id: 'sniper_training',
      name: 'Sniper Program',
      tier: RESEARCH_TIERS.MID,
      category: RESEARCH_CATEGORIES.OFFENSE,
      icon: Target,
      description: 'Elite sniper training and equipment development',
      cost: { funds: 13000, supplies: 38, intel: 18 },
      duration: 5,
      prerequisites: ['weapon_training'],
      benefits: [
        '+50% long-range accuracy',
        'Unlock sniper missions',
        '+25% enemy officer elimination',
        'Unlocks: Precision Equipment'
      ],
      unlocks: ['railgun_tech']
    }
  ],

  // ===== TIER 3: LATE GAME (Cutting Edge) =====
  late: [
    {
      id: 'powered_exoskeleton',
      name: 'Powered Exoskeleton',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.DEFENSE,
      icon: Shield,
      description: 'Mechanical exoskeletons that enhance strength and protection',
      cost: { funds: 30000, supplies: 80, intel: 40 },
      duration: 8,
      prerequisites: ['advanced_armor'],
      benefits: [
        '+40% soldier health',
        '+30% carrying capacity',
        '+20% melee damage',
        'Unlocks: Exo Equipment'
      ],
      unlocks: ['titan_armor']
    },
    {
      id: 'energy_shields',
      name: 'Energy Shield Technology',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.DEFENSE,
      icon: Shield,
      description: 'Personal energy shields that absorb incoming damage',
      cost: { funds: 35000, supplies: 90, intel: 50 },
      duration: 9,
      prerequisites: ['advanced_armor'],
      benefits: [
        'Shields absorb 30% damage',
        'Shield regeneration',
        '+25% explosion resistance',
        'Unlocks: Energy Equipment'
      ],
      unlocks: ['titan_armor']
    },
    {
      id: 'plasma_weapons',
      name: 'Plasma Weaponry',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.OFFENSE,
      icon: Zap,
      description: 'Experimental plasma-based weapons with devastating power',
      cost: { funds: 32000, supplies: 85, intel: 45 },
      duration: 8,
      prerequisites: ['weapon_mods'],
      benefits: [
        '+60% weapon damage',
        'Armor penetration',
        'Area damage effect',
        'Unlocks: Plasma Equipment'
      ],
      unlocks: ['titan_armor']
    },
    {
      id: 'railgun_tech',
      name: 'Railgun Technology',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.OFFENSE,
      icon: Target,
      description: 'Electromagnetic railguns for extreme long-range precision',
      cost: { funds: 28000, supplies: 75, intel: 38 },
      duration: 7,
      prerequisites: ['sniper_training'],
      benefits: [
        '+80% long-range damage',
        'Extreme range',
        'Vehicle penetration',
        'Unlocks: Railgun Equipment'
      ],
      unlocks: []
    },
    {
      id: 'nano_medicine',
      name: 'Nano-Medicine',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.SUPPORT,
      icon: Cpu,
      description: 'Nanobots that provide rapid healing and disease prevention',
      cost: { funds: 26000, supplies: 70, intel: 35 },
      duration: 7,
      prerequisites: ['combat_medic'],
      benefits: [
        '+60% healing efficiency',
        'Instant wound stabilization',
        'Disease immunity',
        'Unlocks: Nano Equipment'
      ],
      unlocks: []
    },
    {
      id: 'mobile_hospital',
      name: 'Mobile Hospital',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.SUPPORT,
      icon: Heart,
      description: 'Deploy field hospitals for rapid medical response',
      cost: { funds: 24000, supplies: 65, intel: 30 },
      duration: 6,
      prerequisites: ['combat_medic', 'field_medicine'],
      benefits: [
        '+50% recovery speed',
        'Can treat multiple soldiers',
        '+30% mission survivability',
        'Unlocks: Medical Facilities'
      ],
      unlocks: []
    },
    {
      id: 'ghost_protocol',
      name: 'Ghost Protocol',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.TACTICS,
      icon: Radio,
      description: 'Ultimate stealth technology with optical camouflage',
      cost: { funds: 33000, supplies: 88, intel: 55 },
      duration: 9,
      prerequisites: ['stealth_ops'],
      benefits: [
        'Active camouflage',
        '-70% detection chance',
        'Silent movement',
        'Unlocks: Ghost Missions'
      ],
      unlocks: []
    },
    {
      id: 'cyber_warfare',
      name: 'Cyber Warfare Suite',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.TECH,
      icon: Lock,
      description: 'Advanced hacking and electronic warfare capabilities',
      cost: { funds: 29000, supplies: 78, intel: 60 },
      duration: 8,
      prerequisites: ['stealth_ops', 'surveillance_network'],
      benefits: [
        'Disable enemy systems',
        '+50% intel theft',
        'Electronic countermeasures',
        'Unlocks: Cyber Missions'
      ],
      unlocks: []
    },
    {
      id: 'ai_tactical',
      name: 'AI Tactical Assistant',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.COMMAND,
      icon: Brain,
      description: 'AI-powered tactical analysis and mission planning',
      cost: { funds: 27000, supplies: 72, intel: 48 },
      duration: 7,
      prerequisites: ['surveillance_network'],
      benefits: [
        '+35% mission success rate',
        'Predictive analysis',
        '+25% resource efficiency',
        'Unlocks: AI Support'
      ],
      unlocks: ['strategic_planning']
    },
    {
      id: 'strategic_planning',
      name: 'Strategic Command Network',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.COMMAND,
      icon: Microscope,
      description: 'Unified command system with real-time strategic coordination',
      cost: { funds: 40000, supplies: 95, intel: 65 },
      duration: 10,
      prerequisites: ['ai_tactical', 'weapon_mods', 'surveillance_network'],
      benefits: [
        '+2 mission slots',
        '+30% intel gathering',
        '+20% all mission success',
        'Unlocks: Strategic Operations'
      ],
      unlocks: ['titan_armor']
    },
    {
      id: 'titan_armor',
      name: 'Project TITAN',
      tier: RESEARCH_TIERS.LATE,
      category: RESEARCH_CATEGORIES.TECH,
      icon: Shield,
      description: 'Ultimate combat system combining all technologies',
      cost: { funds: 50000, supplies: 120, intel: 80 },
      duration: 12,
      prerequisites: ['powered_exoskeleton', 'energy_shields', 'plasma_weapons', 'strategic_planning'],
      benefits: [
        '+100% combat effectiveness',
        'All resistances +50%',
        'Devastating firepower',
        'Mission dominance'
      ],
      unlocks: []
    }
  ]
};

// Helper function to get all research projects as a flat array
export const getAllResearchProjects = () => {
  return [...researchTree.early, ...researchTree.mid, ...researchTree.late];
};

// Helper function to get research by ID
export const getResearchById = (id) => {
  return getAllResearchProjects().find(project => project.id === id);
};

// Helper function to get research by tier
export const getResearchByTier = (tier) => {
  return researchTree[tier] || [];
};

// Helper function to check if research is available (prerequisites met)
export const isResearchAvailable = (projectId, completedResearch = []) => {
  const project = getResearchById(projectId);
  if (!project) return false;
  
  // Check if all prerequisites are completed
  return project.prerequisites.every(prereqId => 
    completedResearch.includes(prereqId)
  );
};

// Helper function to get available research projects
export const getAvailableResearch = (completedResearch = []) => {
  return getAllResearchProjects().filter(project => 
    !completedResearch.includes(project.id) && 
    isResearchAvailable(project.id, completedResearch)
  );
};

// Category color configurations
export const getCategoryConfig = (category) => {
  const configs = {
    defense: {
      color: 'blue',
      textClass: 'text-blue-400',
      bgClass: 'bg-blue-500/20',
      borderClass: 'border-blue-500'
    },
    offense: {
      color: 'red',
      textClass: 'text-red-400',
      bgClass: 'bg-red-500/20',
      borderClass: 'border-red-500'
    },
    support: {
      color: 'green',
      textClass: 'text-green-400',
      bgClass: 'bg-green-500/20',
      borderClass: 'border-green-500'
    },
    tactics: {
      color: 'purple',
      textClass: 'text-purple-400',
      bgClass: 'bg-purple-500/20',
      borderClass: 'border-purple-500'
    },
    command: {
      color: 'yellow',
      textClass: 'text-yellow-400',
      bgClass: 'bg-yellow-500/20',
      borderClass: 'border-yellow-500'
    },
    tech: {
      color: 'cyan',
      textClass: 'text-cyan-400',
      bgClass: 'bg-cyan-500/20',
      borderClass: 'border-cyan-500'
    }
  };
  return configs[category] || configs.defense;
};